/**
 * Shortens an IPv6 address to its compact form (RFC 5952): leading zeros dropped from each group
 * and the longest run of two or more zero groups replaced with `::`. Anything else is returned as is.
 *
 * @example compactIpAddress("2001:0db8:0000:0000:0000:0000:0000:0001") // "2001:db8::1"
 */
export function compactIpAddress(ip: string): string {
  if (!ip.includes(":")) return ip;

  const groups = expandIpv6(ip);
  if (!groups) return ip;

  const trimmed = groups.map((group) => parseInt(group, 16).toString(16));

  // Find the longest run of zero groups; RFC 5952 only collapses runs of two or more.
  let bestStart = -1;
  let bestLength = 0;
  for (let i = 0; i < trimmed.length; i++) {
    if (trimmed[i] !== "0") continue;
    let end = i;
    while (end < trimmed.length && trimmed[end] === "0") end++;
    if (end - i > bestLength) {
      bestStart = i;
      bestLength = end - i;
    }
    i = end;
  }

  if (bestLength < 2) return trimmed.join(":");
  const left = trimmed.slice(0, bestStart).join(":");
  const right = trimmed.slice(bestStart + bestLength).join(":");
  return `${left}::${right}`;
}

/** The eight groups of an IPv6 address, or null when it isn't one. */
function expandIpv6(ip: string): string[] | null {
  const halves = ip.toLowerCase().split("::");
  if (halves.length > 2) return null;

  const left = halves[0] ? halves[0].split(":") : [];
  const right = halves.length === 2 && halves[1] ? halves[1].split(":") : [];
  const missing = 8 - left.length - right.length;
  if (halves.length === 2 ? missing < 1 : missing !== 0) return null;

  const groups = [...left, ...Array<string>(missing).fill("0"), ...right];
  return groups.every((group) => /^[0-9a-f]{1,4}$/.test(group)) ? groups : null;
}
